import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchAllCycles,
  fetchAllEmployees,
  fetchCycleReviewRows,
  computeCycleStats,
  assignManagerToForm,
  createReviewForm,
  type EPRSetupCycle,
  type EPREmployee,
  type EPRReviewRow,
  type EPRCycleStats,
} from '../services/eprSetupService';
import { deleteReviewForm } from '../services/reviewFormService';
import {
  fetchRoundTableGroups,
  createRoundTableGroup,
  updateRoundTableGroup,
  deleteRoundTableGroup,
} from '../services/leaderEPRService';
import type { ReviewWorkflowStatus, RoundTableGroup } from '../types';

// ── Status helpers ─────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<ReviewWorkflowStatus, string> = {
  prep_window: 'Prep Window',
  self_pending: 'Self Pending',
  self_submitted: 'Self Submitted',
  '360_pending': '360 Pending',
  '360_collected': '360 Collected',
  manager_pending: 'Manager Pending',
  manager_submitted: 'Manager Submitted',
  calibration: 'Calibration',
  approved: 'Approved',
  signed_off: 'Signed Off',
};

const STATUS_COLOR: Record<ReviewWorkflowStatus, string> = {
  prep_window: 'bg-gray-100 text-gray-600',
  self_pending: 'bg-amber-100 text-amber-700',
  self_submitted: 'bg-blue-100 text-blue-700',
  '360_pending': 'bg-purple-100 text-purple-700',
  '360_collected': 'bg-purple-100 text-purple-700',
  manager_pending: 'bg-orange-100 text-orange-700',
  manager_submitted: 'bg-indigo-100 text-indigo-700',
  calibration: 'bg-cyan-100 text-cyan-700',
  approved: 'bg-green-100 text-green-700',
  signed_off: 'bg-green-200 text-green-800',
};

const initials = (name: string) =>
  name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

// ── Stat Card ──────────────────────────────────────────────────────────────────

const StatCard = ({
  label, value, sub, accent,
}: {
  label: string; value: string | number; sub?: string; accent?: string;
}) => (
  <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{label}</p>
    <p className={`text-3xl font-bold ${accent ?? 'text-[#011132]'}`}>{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
  </div>
);

// ── Assign Manager Modal ───────────────────────────────────────────────────────

const AssignManagerModal = ({
  open, onClose, onAssign, employees, currentManagerId, revieweeName,
}: {
  open: boolean;
  onClose: () => void;
  onAssign: (managerId: string) => void;
  employees: EPREmployee[];
  currentManagerId?: string;
  revieweeName: string;
}) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(currentManagerId ?? '');

  useEffect(() => {
    setSelected(currentManagerId ?? '');
    setSearch('');
  }, [currentManagerId, open]);

  if (!open) return null;

  const filtered = employees.filter(e =>
    e.name.toLowerCase().includes(search.toLowerCase()) ||
    e.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-bold text-[#011132]">Assign Manager</h3>
            <p className="text-sm text-gray-500">
              Reviewer for <span className="font-medium text-gray-700">{revieweeName}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        <input
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-200"
          placeholder="Search by name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        <div className="max-h-64 overflow-y-auto space-y-1">
          {filtered.map(emp => (
            <button
              key={emp.id}
              onClick={() => setSelected(emp.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                selected === emp.id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'
              }`}
            >
              <div className="w-8 h-8 rounded-full bg-[#011132] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                {initials(emp.name)}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{emp.name}</p>
                <p className="text-xs text-gray-400 truncate">{emp.email}</p>
              </div>
              {selected === emp.id && <span className="ml-auto text-blue-600 text-base">✓</span>}
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">No employees found</p>
          )}
        </div>

        <div className="flex gap-3 mt-5">
          <button
            onClick={onClose}
            className="flex-1 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => { if (selected) onAssign(selected); }}
            disabled={!selected}
            className="flex-1 py-2 rounded-lg bg-[#011132] text-white text-sm font-medium hover:bg-[#0a1a3b] disabled:opacity-40"
          >
            Assign
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Enroll Employees Modal ─────────────────────────────────────────────────────

const EnrollModal = ({
  open, onClose, onEnroll, employees, allEmployees,
}: {
  open: boolean;
  onClose: () => void;
  onEnroll: (employeeIds: string[], managerId?: string) => void;
  employees: EPREmployee[];
  allEmployees: EPREmployee[];
}) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [managerId, setManagerId] = useState('');

  useEffect(() => {
    if (open) { setSelected(new Set()); setSearch(''); setManagerId(''); }
  }, [open]);

  if (!open) return null;

  const filtered = employees.filter(e =>
    e.name.toLowerCase().includes(search.toLowerCase()) ||
    e.email.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-bold text-[#011132]">Enroll Employees</h3>
            <p className="text-sm text-gray-500">Add employees to this EPR cycle</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        <input
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-200"
          placeholder="Search employees..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        <div className="max-h-52 overflow-y-auto space-y-1 mb-4">
          {filtered.map(emp => (
            <button
              key={emp.id}
              onClick={() => toggle(emp.id)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                selected.has(emp.id) ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'
              }`}
            >
              <input type="checkbox" readOnly checked={selected.has(emp.id)} className="rounded border-gray-300 text-blue-600" />
              <div className="w-7 h-7 rounded-full bg-[#011132] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                {initials(emp.name)}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{emp.name}</p>
                <p className="text-xs text-gray-400 truncate">{emp.unitName ?? emp.email}</p>
              </div>
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">All employees already enrolled</p>
          )}
        </div>

        {selected.size > 0 && (
          <div className="mb-4">
            <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">
              Assign Manager (optional)
            </label>
            <select
              value={managerId}
              onChange={e => setManagerId(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="">— No manager assigned yet —</option>
              {allEmployees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
        )}

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={() => onEnroll(Array.from(selected), managerId || undefined)}
            disabled={selected.size === 0}
            className="flex-1 py-2 rounded-lg bg-[#011132] text-white text-sm font-medium hover:bg-[#0a1a3b] disabled:opacity-40"
          >
            Enroll {selected.size > 0 ? `${selected.size} Employee${selected.size > 1 ? 's' : ''}` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Round Table Group Modal ────────────────────────────────────────────────────

const RoundTableGroupModal = ({
  open, onClose, onSave, leaders, existing,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (name: string, participantIds: string[]) => void;
  leaders: EPREmployee[];
  existing?: RoundTableGroup;
}) => {
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) {
      setName(existing?.name ?? '');
      setSelected(new Set(existing?.participantIds ?? []));
      setSearch('');
    }
  }, [open, existing]);

  if (!open) return null;

  const filtered = leaders.filter(l =>
    l.name.toLowerCase().includes(search.toLowerCase()) ||
    l.email.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-bold text-[#011132]">
              {existing ? 'Edit Round Table Group' : 'New Round Table Group'}
            </h3>
            <p className="text-sm text-gray-500">Leaders in this group will rate each other during the round table session</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Group Name</label>
          <input
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            placeholder="e.g. C-Suite, Ops Leaders..."
            value={name}
            onChange={e => setName(e.target.value)}
          />
        </div>

        <div className="mb-1 flex items-center justify-between">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">Participants</label>
          <span className="text-xs text-gray-400">{selected.size} selected</span>
        </div>
        <input
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
          placeholder="Search leaders..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="max-h-52 overflow-y-auto space-y-1 mb-5 border border-gray-100 rounded-lg p-2">
          {filtered.map(l => (
            <button
              key={l.id}
              onClick={() => toggle(l.id)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                selected.has(l.id) ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-gray-50'
              }`}
            >
              <input type="checkbox" readOnly checked={selected.has(l.id)} className="rounded border-gray-300 text-indigo-600" />
              <div className="w-7 h-7 rounded-full bg-indigo-700 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                {initials(l.name)}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{l.name}</p>
                <p className="text-xs text-gray-400 truncate">{l.email}</p>
              </div>
              {selected.has(l.id) && <span className="ml-auto text-indigo-600 text-sm">✓</span>}
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">No leaders found</p>
          )}
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={() => { if (name.trim() && selected.size >= 2) onSave(name.trim(), Array.from(selected)); }}
            disabled={!name.trim() || selected.size < 2}
            className="flex-1 py-2 rounded-lg bg-indigo-700 text-white text-sm font-medium hover:bg-indigo-800 disabled:opacity-40"
          >
            {existing ? 'Save Changes' : `Create Group (${selected.size} leaders)`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ──────────────────────────────────────────────────────────────────

type Tab = 'overview' | 'mapping' | 'tracker' | 'roundtable';

export default function EPRSetupPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.role !== 'superadmin' && user.role !== 'admin' && user.role !== 'hr_admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [cycles, setCycles] = useState<EPRSetupCycle[]>([]);
  const [selectedCycleId, setSelectedCycleId] = useState<string>('');
  const [allEmployees, setAllEmployees] = useState<EPREmployee[]>([]);
  const [reviewRows, setReviewRows] = useState<EPRReviewRow[]>([]);
  const [stats, setStats] = useState<EPRCycleStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [rowsLoading, setRowsLoading] = useState(false);

  const [filterDivision, setFilterDivision] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Round Table state
  const [roundTableGroups, setRoundTableGroups] = useState<RoundTableGroup[]>([]);
  const [rtGroupModal, setRtGroupModal] = useState<{
    open: boolean; existing?: RoundTableGroup;
  }>({ open: false });
  const [rtLoading, setRtLoading] = useState(false);

  const [assignModal, setAssignModal] = useState<{
    open: boolean; formId: string; revieweeName: string; currentManagerId?: string;
  }>({ open: false, formId: '', revieweeName: '' });
  const [enrollModal, setEnrollModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [cycleList, empList] = await Promise.all([fetchAllCycles(), fetchAllEmployees()]);
        setCycles(cycleList);
        setAllEmployees(empList);
        if (cycleList.length > 0) setSelectedCycleId(cycleList[0].id);
      } catch (e) {
        console.error('Failed to load EPR setup data', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const loadRows = useCallback(async (cycleId: string) => {
    if (!cycleId) return;
    setRowsLoading(true);
    try {
      const rows = await fetchCycleReviewRows(cycleId);
      setReviewRows(rows);
      setStats(computeCycleStats(rows));
    } catch (e) {
      console.error('Failed to load review rows', e);
    } finally {
      setRowsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCycleId) loadRows(selectedCycleId);
  }, [selectedCycleId, loadRows]);

  // Load round table groups when cycle changes or tab switches to roundtable
  const loadRoundTableGroups = useCallback(async (cycleId: string) => {
    if (!cycleId) return;
    setRtLoading(true);
    try {
      const groups = await fetchRoundTableGroups(cycleId);
      setRoundTableGroups(groups);
    } finally {
      setRtLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'roundtable' && selectedCycleId) {
      loadRoundTableGroups(selectedCycleId);
    }
  }, [activeTab, selectedCycleId, loadRoundTableGroups]);

  // Leaders = employees with isLeader flag in Firestore users collection
  // We approximate via review rows: anyone with a form who is marked as leader.
  // Since EPREmployee doesn't carry isLeader, we use allEmployees — HR knows who leaders are.
  // For the modal we show all employees and let HR pick.
  const leaderEmployees = allEmployees; // HR selects from full list

  const divisions = Array.from(
    new Set(reviewRows.map(r => r.unitName).filter(Boolean) as string[])
  ).sort();

  const filteredRows = reviewRows.filter(r => {
    if (filterDivision && r.unitName !== filterDivision) return false;
    if (filterStatus && r.status !== filterStatus) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!r.revieweeName.toLowerCase().includes(q) && !(r.managerName ?? '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const unenrolledEmployees = (() => {
    const enrolledIds = new Set(reviewRows.map(r => r.revieweeId));
    return allEmployees.filter(e => !enrolledIds.has(e.id));
  })();

  const selectedCycle = cycles.find(c => c.id === selectedCycleId);

  const handleAssign = async (managerId: string) => {
    setSaving(true);
    try {
      await assignManagerToForm(assignModal.formId, managerId);
      await loadRows(selectedCycleId);
      setAssignModal({ open: false, formId: '', revieweeName: '' });
      showToast('Manager assigned successfully');
    } catch (e) {
      console.error(e);
      showToast('Failed to assign manager');
    } finally {
      setSaving(false);
    }
  };

  const handleEnroll = async (employeeIds: string[], managerId?: string) => {
    setSaving(true);
    try {
      await Promise.all(
        employeeIds.map(eid => {
          const emp = allEmployees.find(e => e.id === eid);
          return createReviewForm(selectedCycleId, eid, emp?.name ?? '', managerId);
        })
      );
      await loadRows(selectedCycleId);
      setEnrollModal(false);
      showToast(`${employeeIds.length} employee(s) enrolled`);
    } catch (e) {
      console.error(e);
      showToast('Failed to enroll employees');
    } finally {
      setSaving(false);
    }
  };

  const handleUnenroll = async (formId: string, revieweeName: string) => {
    if (!confirm(`Remove "${revieweeName}" from this EPR cycle? This will permanently delete their review form and all associated data.`)) return;
    setSaving(true);
    try {
      await deleteReviewForm(formId);
      await loadRows(selectedCycleId);
      showToast(`${revieweeName} removed from cycle`);
    } catch (e) {
      console.error(e);
      showToast('Failed to remove employee');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRtGroup = async (name: string, participantIds: string[]) => {
    if (!user) return;
    setSaving(true);
    try {
      if (rtGroupModal.existing) {
        await updateRoundTableGroup(rtGroupModal.existing.id, name, participantIds);
        showToast('Group updated');
      } else {
        await createRoundTableGroup(selectedCycleId, name, participantIds, user.id);
        showToast('Group created');
      }
      setRtGroupModal({ open: false });
      await loadRoundTableGroups(selectedCycleId);
    } catch (e) {
      console.error(e);
      showToast('Failed to save group');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRtGroup = async (groupId: string) => {
    if (!confirm('Delete this round table group?')) return;
    setSaving(true);
    try {
      await deleteRoundTableGroup(groupId);
      showToast('Group deleted');
      await loadRoundTableGroups(selectedCycleId);
    } catch (e) {
      console.error(e);
      showToast('Failed to delete group');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-[#011132] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading EPR Setup...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f9fb]">
      {/* Page header */}
      <div className="bg-white border-b border-gray-200 px-8 py-5 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#011132]">EPR Setup</h1>
          <p className="text-sm text-gray-400 mt-0.5">Manage cycles, enroll employees &amp; assign reviewers</p>
        </div>
        {selectedCycle && (
          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
            selectedCycle.status === 'active'
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-500'
          }`}>
            {selectedCycle.status}
          </span>
        )}
      </div>

      <div className="px-8 py-6 max-w-6xl mx-auto">
        {/* Cycle Selector */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">EPR Cycle</label>
            <select
              value={selectedCycleId}
              onChange={e => setSelectedCycleId(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium text-[#011132] bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 min-w-[220px]"
            >
              {cycles.map(c => (
                <option key={c.id} value={c.id}>{c.name} — {c.period}</option>
              ))}
            </select>
          </div>

          {activeTab === 'mapping' && (
            <button
              onClick={() => setEnrollModal(true)}
              disabled={unenrolledEmployees.length === 0}
              className="mt-5 px-4 py-2 bg-[#011132] text-white text-sm font-semibold rounded-lg hover:bg-[#0a1a3b] disabled:opacity-40 flex items-center gap-2"
            >
              + Enroll Employees
              {unenrolledEmployees.length > 0 && (
                <span className="bg-white/20 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {unenrolledEmployees.length} available
                </span>
              )}
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          {([
            { id: 'overview', label: 'Cycle Overview' },
            { id: 'mapping', label: 'Employee Mapping' },
            { id: 'tracker', label: 'Review Status Tracker' },
            { id: 'roundtable', label: '🔵 Round Table Groups' },
          ] as { id: Tab; label: string }[]).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-[#011132] text-[#011132]'
                  : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {rowsLoading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-6 h-6 border-4 border-[#011132] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* ── Tab: Cycle Overview ── */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {stats ? (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                      <StatCard label="Total Enrolled" value={stats.total} />
                      <StatCard label="Completed" value={stats.completed} accent="text-green-600" />
                      <StatCard label="Pending Self" value={stats.pendingSelf} accent="text-amber-600" />
                      <StatCard label="Pending Manager" value={stats.pendingManager} accent="text-orange-600" />
                      <StatCard label="Calibration" value={stats.inCalibration} accent="text-cyan-600" />
                      <StatCard
                        label="Avg Score"
                        value={stats.avgScore != null ? stats.avgScore.toFixed(2) : '—'}
                        sub={`${stats.completionPct}% complete`}
                        accent="text-blue-700"
                      />
                    </div>

                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="font-bold text-[#011132]">Cycle Completion</h3>
                        <span className="text-sm font-bold text-blue-600">{stats.completionPct}%</span>
                      </div>
                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-600 rounded-full transition-all duration-500"
                          style={{ width: `${stats.completionPct}%` }}
                        />
                      </div>
                      <div className="grid grid-cols-4 gap-3 mt-5">
                        {[
                          { label: 'Self Review', count: stats.pendingSelf, color: 'bg-amber-400' },
                          { label: 'Manager Review', count: stats.pendingManager, color: 'bg-orange-400' },
                          { label: 'Calibration', count: stats.inCalibration, color: 'bg-cyan-400' },
                          { label: 'Completed', count: stats.completed, color: 'bg-green-500' },
                        ].map(s => (
                          <div key={s.label} className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${s.color}`} />
                            <div>
                              <p className="text-xs text-gray-500">{s.label}</p>
                              <p className="text-sm font-bold text-[#011132]">{s.count}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {selectedCycle && (
                      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                        <h3 className="font-bold text-[#011132] mb-4">Cycle Details</h3>
                        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                          {[
                            { label: 'Name', value: selectedCycle.name },
                            { label: 'Period', value: selectedCycle.period },
                            { label: 'Status', value: selectedCycle.status },
                            { label: 'Start Date', value: selectedCycle.startDate ?? '—' },
                          ].map(item => (
                            <div key={item.label}>
                              <dt className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{item.label}</dt>
                              <dd className="text-sm font-medium text-gray-800 mt-0.5">{item.value}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100">
                    <p className="text-gray-400 text-sm">No review forms found for this cycle.</p>
                    <p className="text-gray-400 text-xs mt-1">Switch to "Employee Mapping" to enroll employees.</p>
                  </div>
                )}
              </div>
            )}

            {/* ── Tab: Employee Mapping ── */}
            {activeTab === 'mapping' && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-3">
                  <input
                    type="text"
                    placeholder="Search employee..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 w-56"
                  />
                  <select
                    value={filterDivision}
                    onChange={e => setFilterDivision(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="">All Divisions</option>
                    {divisions.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <select
                    value={filterStatus}
                    onChange={e => setFilterStatus(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="">All Statuses</option>
                    {Object.entries(STATUS_LABEL).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                  {(filterDivision || filterStatus || searchQuery) && (
                    <button
                      onClick={() => { setFilterDivision(''); setFilterStatus(''); setSearchQuery(''); }}
                      className="text-xs text-gray-400 hover:text-gray-600 px-2"
                    >
                      Clear filters
                    </button>
                  )}
                  <span className="ml-auto text-xs text-gray-400 self-center">
                    {filteredRows.length} of {reviewRows.length} employees
                  </span>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Employee</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Division</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Manager Reviewer</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                        <th className="px-5 py-3" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {filteredRows.map(row => (
                        <tr key={row.formId} className="hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-[#011132] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                                {initials(row.revieweeName)}
                              </div>
                              <div>
                                <p className="font-medium text-gray-800">{row.revieweeName}</p>
                                <p className="text-xs text-gray-400">{row.revieweeEmail}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-gray-600">{row.unitName ?? '—'}</td>
                          <td className="px-5 py-3.5">
                            {row.managerName
                              ? <span className="font-medium text-gray-700">{row.managerName}</span>
                              : <span className="text-amber-600 text-xs font-medium">⚠ Not assigned</span>}
                          </td>
                          <td className="px-5 py-3.5">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_COLOR[row.status]}`}>
                              {STATUS_LABEL[row.status]}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            <div className="flex items-center justify-end gap-3">
                            <button
                              onClick={() => setAssignModal({
                                open: true,
                                formId: row.formId,
                                revieweeName: row.revieweeName,
                                currentManagerId: row.managerId,
                              })}
                              className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                            >
                              {row.managerId ? 'Change Manager' : 'Assign Manager'}
                            </button>
                            <button
                              onClick={() => handleUnenroll(row.formId, row.revieweeName)}
                              disabled={saving}
                              className="text-xs font-semibold text-red-500 hover:text-red-700 transition-colors disabled:opacity-40"
                            >
                              Remove
                            </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredRows.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-5 py-12 text-center text-gray-400 text-sm">
                            {reviewRows.length === 0
                              ? 'No employees enrolled in this cycle yet.'
                              : 'No results match your filters.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Tab: Review Status Tracker ── */}
            {activeTab === 'tracker' && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-3">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 w-56"
                  />
                  <select
                    value={filterDivision}
                    onChange={e => setFilterDivision(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="">All Divisions</option>
                    {divisions.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <select
                    value={filterStatus}
                    onChange={e => setFilterStatus(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="">All Statuses</option>
                    {Object.entries(STATUS_LABEL).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                  {(filterDivision || filterStatus || searchQuery) && (
                    <button
                      onClick={() => { setFilterDivision(''); setFilterStatus(''); setSearchQuery(''); }}
                      className="text-xs text-gray-400 hover:text-gray-600 px-2"
                    >
                      Clear
                    </button>
                  )}
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
                  <table className="w-full text-sm min-w-[900px]">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Reviewee</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Division</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Manager</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Self ✓</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Mgr ✓</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Score</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {filteredRows.map(row => (
                        <tr key={row.formId} className="hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-[#011132] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                                {initials(row.revieweeName)}
                              </div>
                              <p className="font-medium text-gray-800">{row.revieweeName}</p>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-gray-500 text-xs">{row.unitName ?? '—'}</td>
                          <td className="px-5 py-3.5 text-gray-600 text-xs">
                            {row.managerName ?? <span className="text-amber-500">Unassigned</span>}
                          </td>
                          <td className="px-4 py-3.5 text-center">
                            {row.selfReviewSubmitted
                              ? <span className="text-green-500 font-bold">✓</span>
                              : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-4 py-3.5 text-center">
                            {row.managerReviewSubmitted
                              ? <span className="text-green-500 font-bold">✓</span>
                              : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-4 py-3.5 text-right font-bold text-[#011132]">
                            {row.finalScore != null
                              ? row.finalScore.toFixed(2)
                              : row.computedScore != null
                              ? <span className="text-gray-400">{row.computedScore.toFixed(2)}</span>
                              : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-5 py-3.5">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_COLOR[row.status]}`}>
                              {STATUS_LABEL[row.status]}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {filteredRows.length === 0 && (
                        <tr>
                          <td colSpan={7} className="px-5 py-12 text-center text-gray-400 text-sm">
                            No results match your filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {/* ── Tab: Round Table Groups ── */}
            {activeTab === 'roundtable' && (
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-bold text-[#011132]">Leader Round Table Groups</h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                      Define which leaders review each other in the round table session. Leaders only see and rate peers in their group.
                    </p>
                  </div>
                  <button
                    onClick={() => setRtGroupModal({ open: true })}
                    className="px-4 py-2 bg-indigo-700 text-white text-sm font-semibold rounded-lg hover:bg-indigo-800 flex items-center gap-2"
                  >
                    + New Group
                  </button>
                </div>

                {rtLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="w-6 h-6 border-4 border-indigo-700 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : roundTableGroups.length === 0 ? (
                  <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center">
                    <div className="text-4xl mb-3">🔵</div>
                    <p className="text-gray-700 font-medium">No round table groups yet</p>
                    <p className="text-gray-400 text-sm mt-1 max-w-sm mx-auto">
                      Create groups to define which leaders will rate each other. Without groups, all leaders will rate all other leaders in this cycle.
                    </p>
                    <button
                      onClick={() => setRtGroupModal({ open: true })}
                      className="mt-4 px-4 py-2 bg-indigo-700 text-white text-sm font-semibold rounded-lg hover:bg-indigo-800"
                    >
                      + Create First Group
                    </button>
                  </div>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {roundTableGroups.map(group => {
                      const participants = group.participantIds
                        .map(pid => allEmployees.find(e => e.id === pid))
                        .filter(Boolean) as EPREmployee[];
                      return (
                        <div key={group.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-bold text-[#011132]">{group.name}</h3>
                              <p className="text-xs text-gray-400 mt-0.5">{participants.length} participants</p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setRtGroupModal({ open: true, existing: group })}
                                className="text-xs text-blue-600 hover:text-blue-800 font-semibold"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteRtGroup(group.id)}
                                className="text-xs text-red-500 hover:text-red-700 font-semibold"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                          <div className="space-y-2">
                            {participants.map(p => (
                              <div key={p.id} className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-indigo-700 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                                  {initials(p.name)}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-700 truncate">{p.name}</p>
                                  <p className="text-xs text-gray-400 truncate">{p.email}</p>
                                </div>
                              </div>
                            ))}
                            {participants.length === 0 && (
                              <p className="text-xs text-amber-500">⚠ Participants not found — may have been removed</p>
                            )}
                          </div>
                          <div className="mt-4 pt-3 border-t border-gray-100">
                            <p className="text-xs text-gray-400">
                              Each member rates the other {Math.max(0, participants.length - 1)} peer{participants.length !== 2 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Unassigned leaders warning */}
                {roundTableGroups.length > 0 && (() => {
                  const assignedIds = new Set(roundTableGroups.flatMap(g => g.participantIds));
                  const unassigned = reviewRows.filter(r => !assignedIds.has(r.revieweeId));
                  if (unassigned.length === 0) return null;
                  return (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <p className="text-sm font-semibold text-amber-800">
                        ⚠ {unassigned.length} enrolled employee{unassigned.length > 1 ? 's' : ''} not in any round table group
                      </p>
                      <p className="text-xs text-amber-600 mt-1">
                        {unassigned.map(r => r.revieweeName).join(', ')}
                      </p>
                    </div>
                  );
                })()}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      <AssignManagerModal
        open={assignModal.open}
        onClose={() => setAssignModal({ open: false, formId: '', revieweeName: '' })}
        onAssign={handleAssign}
        employees={allEmployees}
        currentManagerId={assignModal.currentManagerId}
        revieweeName={assignModal.revieweeName}
      />
      <EnrollModal
        open={enrollModal}
        onClose={() => setEnrollModal(false)}
        onEnroll={handleEnroll}
        employees={unenrolledEmployees}
        allEmployees={allEmployees}
      />
      <RoundTableGroupModal
        open={rtGroupModal.open}
        onClose={() => setRtGroupModal({ open: false })}
        onSave={handleSaveRtGroup}
        leaders={leaderEmployees}
        existing={rtGroupModal.existing}
      />

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-[#011132] text-white text-sm font-medium px-5 py-3 rounded-xl shadow-2xl">
          {toast}
        </div>
      )}

      {saving && (
        <div className="fixed inset-0 z-[90] bg-black/10 flex items-center justify-center">
          <div className="w-6 h-6 border-4 border-[#011132] border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
}
